import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Upload } from "lucide-react";
import { toast } from "sonner";

export const KycSection = () => {
  const [status, setStatus] = useState(null);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [docUrl, setDocUrl] = useState("");
  const [docName, setDocName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/trust/kyc/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);

  const uploadId = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setDocUrl(data.url); setDocName(data.filename || file.name);
      toast.success("ID document uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/trust/kyc/submit", { full_name: fullName, phone, id_document_url: docUrl });
      setStatus((s) => ({ ...s, status: data.status }));
      toast.success("Submitted — you'll be verified shortly");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Submission failed");
    } finally { setBusy(false); }
  };

  if (!status) return null;
  if (status.status === "verified") {
    return (
      <div data-testid="kyc-verified" className="mb-6 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm text-emerald-400 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4" /> Identity verified — payouts enabled
      </div>
    );
  }
  return (
    <div data-testid="kyc-section" className="mb-8 rounded-xl border border-[#27272A] bg-[#121212] p-6">
      <div className="flex items-center gap-2 font-bold text-sm uppercase tracking-widest">
        <ShieldCheck className="h-4 w-4 text-[#FF3B30]" /> Verify your identity
      </div>
      <p className="text-xs text-neutral-500 mt-1">
        Required once before connecting payouts — protects buyers and keeps VISMARKS fraud-free. Accepted: PDF, Word, or a photo/scan (JPG, PNG, HEIC) — max 10MB. Only you and VISMARKS admins can open it.
      </p>
      {status.status === "pending" ? (
        <div data-testid="kyc-pending" className="mt-3 text-sm text-amber-400">⏳ Under review — usually within 24 hours</div>
      ) : (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input data-testid="kyc-name" placeholder="Full legal name (as on bank account)" value={fullName} onChange={(e) => setFullName(e.target.value)} className="bg-black/40 border-[#27272A]" />
          <Input data-testid="kyc-phone" placeholder="Phone number" value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-black/40 border-[#27272A]" />
          <div className="flex gap-2">
            <label className="flex-1">
              <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/*" className="hidden" data-testid="kyc-id-input" onChange={(e) => uploadId(e.target.files?.[0])} />
              <span className={`inline-flex w-full items-center justify-center gap-2 h-10 rounded-md border text-xs font-bold cursor-pointer truncate px-2 ${docUrl ? "border-emerald-500/40 text-emerald-400" : "border-[#27272A] text-neutral-300 hover:border-neutral-500"}`} data-testid="kyc-id-label" title={docName}>
                <Upload className="h-3.5 w-3.5 shrink-0" /> {uploading ? "Uploading…" : docUrl ? `✓ ${docName}` : "Upload govt ID"}
              </span>
            </label>
            <Button data-testid="kyc-submit" onClick={submit} disabled={busy || !fullName || !phone || !docUrl}
              className="rounded-md bg-[#FF3B30] text-white font-bold hover:bg-[#e0342a]">
              {busy ? "…" : "Submit"}
            </Button>
          </div>
        </div>
      )}
      {status.status === "rejected" && <div className="mt-2 text-xs text-[#FF3B30]">Previous submission rejected{status.note ? `: ${status.note}` : ""} — resubmit above.</div>}
    </div>
  );
};
